import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, Nav } from "react-bootstrap";

import { getForm4 } from "../../apis/Form";
import Avatar from "../../components/Avatar";
import PaginationControls from "../../components/PaginationControls";
import useDarkMode from "../../hooks/useDarkMode";
import { formatCurrencyKOnly } from "../../utils/formatters";

const toHoldingTrade = (share, guruStatus) => ({
  date: share.date || null,
  numshare: share.numshare ?? null,
  totvalue: share.totvalue ?? null,
  transactioncode: share.transactioncode || (guruStatus === "buy" ? "P" : "S"),
});

export default function InsiderListSection({
  ticker: propTicker,
  activeMainTab,
  onSelectionChange,
}) {
  const { ticker: urlTicker } = useParams();
  const ticker = propTicker || urlTicker;
  const [form4Data, setForm4Data] = useState([]);
  const [isInsiderLoading, setIsInsiderLoading] = useState(false);
  const [guruStatus, setGuruStatus] = useState("buy");
  const [guruPage, setGuruPage] = useState(1);
  const gurusPerPage = 5;

  const isDarkMode = useDarkMode();

  useEffect(() => {
    if (!ticker || activeMainTab !== "insider") return;
    let isMounted = true;

    const loadInsiderData = async () => {
      setIsInsiderLoading(true);
      try {
        const response = await getForm4(ticker);
        const data = await response.json();
        if (isMounted) {
          setForm4Data(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error fetching insider info:", err);
        if (isMounted) setForm4Data([]);
      } finally {
        if (isMounted) setIsInsiderLoading(false);
      }
    };

    loadInsiderData();
    return () => { isMounted = false; };
  }, [ticker, activeMainTab]);

  useEffect(() => {
    setGuruPage(1);
  }, [ticker, guruStatus]);

  const filteredGurus = useMemo(() => {
    if (!form4Data?.length) return [];

    const targetCode = guruStatus === "buy" ? "P" : "S";
    return form4Data
      .filter((item) => item.transactioncode === targetCode)
      .map((item) => {
        const roles = [];
        if (item.isdirector) roles.push("디렉터");
        if (item.isofficer) roles.push("경영진");
        if (item.istenpercent) roles.push("대주주");
        if (item.isother) roles.push("기타");

        return {
          guru: {
            nickname: roles.length > 0 ? roles.join(", ") : "기타",
            leader: item.reportername,
            institution: item.title || "Insider",
            imagefile: null,
            percent: item.transactionamount,
          },
          transactionend: item.transactionend,
          numShares: [{
            date: item.reportdate,
            numshare: item.transactionshares || 0,
            totvalue: item.transactionamount || 0,
            pubdate: item.reportdate,
            transactioncode: targetCode,
          }],
        };
      });
  }, [form4Data, guruStatus]);

  const insiderBarTrades = useMemo(() => {
    if (!form4Data?.length) return [];

    const targetCode = guruStatus === "buy" ? "P" : "S";
    return form4Data
      .filter((item) => item.transactioncode === targetCode)
      .map((item) => ({
        date: item.reportdate,
        transactioncode: item.transactioncode,
        transactionamount: item.transactionamount || 0,
        reportername: item.reportername,
        title: item.title,
      }))
      .filter((trade) => trade.date);
  }, [form4Data, guruStatus]);

  const totalGuruPages = Math.ceil(filteredGurus.length / gurusPerPage);
  const paginatedGurus = useMemo(() => {
    const start = (guruPage - 1) * gurusPerPage;
    return filteredGurus.slice(start, start + gurusPerPage);
  }, [filteredGurus, guruPage]);

  const handleStatusClick = useCallback((e, status) => {
    e.preventDefault();
    if (guruStatus !== status) {
      setGuruStatus(status);
      setGuruPage(1);
    }
  }, [guruStatus]);

  const handlePageChange = useCallback((page) => {
    setGuruPage(page);
  }, []);

  const emitSelection = useCallback((entry) => {
    const guru = entry?.guru || entry;
    const allShares = entry?.numShares || [];
    const selectedName = guru.leader || guru.nickname || guru.institution || null;

    if (!onSelectionChange) return;

    const trades = allShares
      .filter(Boolean)
      .map((share) => toHoldingTrade(share, guruStatus))
      .filter((trade) => trade.date);

    onSelectionChange({
      categoryKey: "INSIDER",
      status: guruStatus,
      selectedName,
      selectedTrades: trades,
      barTrades: insiderBarTrades,
      isInsider: true,
      useAllInsiderTrades: true,
    });
  }, [guruStatus, insiderBarTrades, onSelectionChange]);

  useEffect(() => {
    if (!onSelectionChange) return;
    if (!paginatedGurus.length) {
      onSelectionChange({
        categoryKey: "INSIDER",
        status: guruStatus,
        selectedName: null,
        selectedTrades: [],
        barTrades: insiderBarTrades,
        isInsider: true,
        useAllInsiderTrades: true,
      });
      return;
    }

    emitSelection(paginatedGurus[0]);
  }, [onSelectionChange, paginatedGurus, emitSelection, guruStatus, insiderBarTrades]);

  if (activeMainTab && activeMainTab !== "insider") {
    return (
      <Card className="h-100 shadow-sm border-0" style={{ minHeight: "530px" }}>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <p className="text-secondary m-0">해당 탭의 투자자 정보가 없습니다.</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="card-people h-100 shadow-sm border-0" style={{ minHeight: "530px" }}>
      <Card.Body className="h-100 d-flex flex-column">
        <Nav className="nav-line mb-2 align-items-center">
          <Nav.Link
            href="#"
            active={guruStatus === "buy"}
            onClick={(e) => handleStatusClick(e, "buy")}
          >
            매수
          </Nav.Link>
          <Nav.Link
            href="#"
            active={guruStatus === "sell"}
            onClick={(e) => handleStatusClick(e, "sell")}
          >
            매도
          </Nav.Link>
          <Nav.Link
            href="#"
            className={isDarkMode ? "ms-auto active" : "ms-auto text-secondary active"}
            tabIndex={-1}
            style={{ color: isDarkMode ? "rgba(255, 255, 255, 0.75)" : undefined }}
          >
            거래액
          </Nav.Link>
        </Nav>

        <ul className="people-group flex-grow-1 d-flex flex-column">
          {isInsiderLoading ? (
            <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-muted" style={{ minHeight: "200px" }}>
              <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div style={{ fontSize: "12px" }}>데이터를 불러오는 중입니다...</div>
            </div>
          ) : paginatedGurus.map((entry, index) => {
            const guru = entry?.guru || entry;
            const itemKey = `INSIDER-${guruStatus}-${guruPage}-${index}`;

            return (
              <li
                key={itemKey}
                className="people-item d-flex align-items-center"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => emitSelection(entry)}
                onFocus={() => emitSelection(entry)}
                onClick={() => emitSelection(entry)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    emitSelection(entry);
                  }
                }}
              >
                <div className="flex-shrink-0">
                  <Avatar />
                </div>
                <div className="people-body flex-grow-1 px-2" style={{ minWidth: 0 }}>
                  <span className="d-block text-truncate" style={{ fontSize: "12px", marginBottom: "2px", color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : undefined }}>{guru.nickname}</span>
                  <h6 className="m-0 text-truncate" style={{ fontSize: "14px", color: isDarkMode ? "#ffffff" : undefined }}>
                    {guru.leader}
                  </h6>
                  <span className="d-block text-truncate" style={{ fontSize: "11px", color: isDarkMode ? "rgba(255, 255, 255, 0.6)" : undefined }}>{guru.institution}</span>
                </div>
                <div
                  className="flex-shrink-0"
                  style={{
                    minWidth: "60px",
                    textAlign: "right",
                    color: isDarkMode ? "rgba(255, 255, 255, 0.85)" : undefined,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <span>{formatCurrencyKOnly(guru.percent)}</span>
                  <span
                    style={{
                      fontSize: "11px",
                      color: isDarkMode ? "rgba(255, 255, 255, 0.55)" : "rgba(100, 116, 139, 0.95)",
                    }}
                  >
                    {entry?.transactionend}
                  </span>
                </div>
              </li>
            );
          })}
          {!filteredGurus.length && (
            <li className="people-item flex-grow-1 d-flex align-items-center justify-content-center text-center">
              <div className="people-body w-100">
                <span style={{ color: isDarkMode ? "rgba(255, 255, 255, 0.7)" : undefined }}>해당 상태의 데이터가 없습니다.</span>
              </div>
            </li>
          )}
        </ul>
        {filteredGurus.length > 0 && (
          <PaginationControls
            currentPage={guruPage}
            totalPages={totalGuruPages}
            onPrev={() => handlePageChange(guruPage - 1)}
            onNext={() => handlePageChange(guruPage + 1)}
            className="mt-2"
          />
        )}
      </Card.Body>
    </Card>
  );
}
